/*
 * Copyright (c) MLCommons and its affiliates.
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

/*
 * Copyright (c) Facebook, Inc. and its affiliates.
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from "react";
import { Container, Col } from "react-bootstrap";

const DataPolicyPage = () => (
  <Container className="mb-5 pb-5">
    <Col className="m-auto" lg={9}>
      <h1 className="my-4 pt-3 text-uppercase">Data Policy</h1>
      <p>Effective Date: December 1, 2020</p>
      <br />
      <p>
        Dynabench is a collaborative research project from Facebook, Inc.
        (“Facebook,” “us,” or “our”). The aim of the Dynabench website (the
        “Website”) is to investigate the ability of AI models to make accurate
        predictions in different contexts using different terminology and
        expressions, and to determine to what extent existing AI models can
        potentially be confused or misled into making wrong predictions based on
        the wording used. The data controller responsible for the personal data
        of individuals in the European Union is Facebook Ireland Limited.
      </p>
      <p>
        This Data Policy describes our practices for handling your information
        collected in connection with the Website.
      </p>
      <p>
        <strong>What kind of information do we collect?</strong>
      </p>
      <p>
        When you interact with us through the Website, we collect or receive
        from you the following types of information:
      </p>
      <ul>
        <li>
          <strong>Identifiers.</strong> This includes information such as your:
          <br />
          <ul>
            <li>
              Contact information: Any data you provide us that would allow us
              to contact you, such as your name or email address. We collect
              this data directly from you.
            </li>
            <li>
              Account login information: Any data required for access to and
              management of your account, such as your user name (which need not
              be your real name), email address and password. For some areas of
              website, you will need to create an account on our Website in
              order to participate. We collect this data directly from you.
            </li>
            <li>
              Device data: Data about your computer system or other device that
              you use to access our Website including device attributes
              (information such as the operating system, hardware and software
              versions), device operations (information about operations and
              behaviors performed on the device), device identifiers (such as
              device IDs and other identifiers) and your IP address. We collect
              this data automatically.
            </li>
          </ul>
          We use the data to allow you to participate in the testing and
          benchmarking of the AI models, as well as to manage your account and
          ensure its security.
        </li>

        <li>
          <strong>Internet or Other Similar Network Activity.</strong> This
          includes usage data such as which pages or content on our Website you
          view and for how long, and other similar data and statistics about
          your interactions, such as content response times, download errors,
          and length of visits to certain pages on our Website, as well as your
          operating system type and web browser type and version. We use this
          data for account management, personalization and security purposes. We
          collect this information automatically when you use the Website.
        </li>

        <li>
          <strong>
            User-generated content (such as text, audio, electronic, visual or
            similar content uploaded by the user).
          </strong>{" "}
          This includes any content or submission that you upload, post or
          otherwise share with us on our Website. Examples include any text,
          photos, videos, AI models, tasks, software, services, applications,
          datasets, or other media or content. We collect this data directly
          from you and use the data to test the ability of our existing AI
          systems to make accurate predictions, regardless of the context or
          terminology that you used.
          <br />
          We ask that you do not submit or provide any confidential, private or
          personal information when interacting with AI systems since the
          information provided may be later made public as part of an open
          source dataset that we will make available on our Website. We also ask
          that you not provide any sensitive personal information (e.g.
          information related to racial or ethnic origin, political opinions,
          religion or other beliefs, health, biometrics or genetic
          characteristics, criminal background or trade union membership) on or
          through the Website, or otherwise to us. If you provide us with any
          sensitive personal information to us when you use the Website, we will
          use such information in accordance with this Data Policy.
        </li>

        <li>
          <strong>Inferences.</strong> Since the purpose of the website is to
          test the ability of AI systems to make accurate predictions in
          different contexts using different terminology and expressions, we
          draw inferences from the data that you provide as user-generated
          content. We use this data to further train these AI models and systems
          and to release new datasets. If you submit to us any AI models, tasks,
          software, services, applications, or datasets, we may use such
          submissions to draw inferences based on data provided to us. As
          mentioned above in the section relating to user-generated content, we
          ask that you do not submit or provide any confidential, private or
          personal information when interacting with the AI systems.
        </li>
      </ul>

      <p>
        <strong>What are the sources of this information?</strong>
      </p>
      <p>
        The categories of sources from which we have collected or received
        information are as follows:
      </p>
      <ul>
        <li>
          You: We collect the content, communications, AI models, tasks,
          software, services, applications, datasets and other information you
          provide when you use the Website, including your interactions with the
          AI systems.Information collected from other users is collected in the
          same way in order to train AI models.
        </li>
      </ul>

      <p>
        <strong>How do we use this information?</strong>
      </p>
      <p>
        We will use the information described above for the following purposes:
      </p>
      <ul>
        <li>
          For research and development purposes, including training and
          improving AI models and developing new AI models and systems.
        </li>
        <li>
          To improve our services, including analyzing how user-generated
          content has confused or misled AI models into making wrong
          predictions. We analyze and review interactions with AI systems using
          human and machine processes to improve, troubleshoot, and train AI
          systems.
        </li>
        <li>
          To provide, operate and improve the Website, including establishing
          and maintaining your account on the Website, provide improved
          administration of our Website, send you service-related email
          communications, identify you as a user in our system and provide
          support and maintenance.
        </li>
        <li>
          As necessary or appropriate to comply with applicable laws, lawful
          requests, and legal process, such as to respond to subpoenas or
          requests from government authorities.
        </li>
        <li>
          For compliance, fraud prevention, and safety purposes, including to
          protect our, your or others’ rights, privacy, safety or property
          (including by making and defending legal claims); enforce the terms
          and conditions that govern the Website and protect, investigate and
          deter against fraudulent, harmful, unauthorized, unethical or illegal
          activity.
        </li>
      </ul>

      <p>
        <strong>How do we share this information?</strong>
      </p>
      <p>
        If you choose to engage in interactions with the AI systems, you should
        be aware that any information you share may be read, collected, or used
        by other users in connection with open source datasets that we will make
        available for download on our Website. You should therefore use caution
        in disclosing any confidential, private or personal information while
        interacting with the AI systems.
      </p>
      <p>
        Any submission or information that you supply as user-generated content
        may be accessible to others to read, collect, re-publish, and otherwise
        freely use and disclose. We are not responsible for the information you
        choose to submit and we will only take down, remove, or edit
        user-generated content in our sole discretion, except as otherwise
        required by applicable law or regulation.
      </p>
      <p>
        If you include in your user-generated content any information relating
        to others, you represent that you have full permission and authority to
        do so.
      </p>
      <p>
        In addition to the above, there are certain circumstances in which we
        may share your information with certain third parties without further
        notice to you, as set forth below:
      </p>
      <ul>
        <li>
          Authorized third-party vendors and service providers. We share your
          information with third-party vendors and service providers who support
          the Website, such as by providing technical infrastructure services
          and data processing. In particular, we use third-party vendors and
          service providers to analyze and review interactions conducted on the
          Website using human and machine processes in order to improve,
          troubleshoot, and train AI models.
        </li>
        <li>
          Research purposes. We share information for research purposes with
          academic and research institutions, as well as independent
          researchers. Information we may share for research purposes include
          user-generated content you have uploaded or posted on the Website, as
          well as your interactions with AI systems. We do not share your
          account login information or name.
        </li>
        <li>
          Legal purposes. We may disclose information to respond to subpoenas,
          court orders, legal process, law enforcement requests, legal claims or
          government inquiries, detect fraud, and to protect and defend the
          rights, interests, safety, and security of the Website, our
          affiliates, owner, users, or the public.
        </li>
        <li>
          Business transfers. We may share your information in connection with a
          substantial corporate transaction, such as the sale of a website, a
          merger, consolidation, asset sale, or in the unlikely event of
          bankruptcy.
        </li>
        <li>
          With your consent. We may share information for any other purposes
          disclosed to you at the time we collect the information or pursuant to
          your consent.
        </li>
      </ul>

      <p>
        <strong>How do we protect this information?</strong>
      </p>
      <p>
        We take measures to help protect information from loss, theft, misuse
        and unauthorized access, disclosure, alteration, and destruction.
        However, no data storage system or transmission of data over the
        Internet or any other public network is guaranteed to be 100 percent
        secure.
      </p>
      <p>
        Accordingly, we cannot guarantee the security of your personal
        information transmitted electronically and we cannot promise that your
        use of our Website will be completely safe. Any transmission of personal
        information is at your own risk. Where we have given you (or where you
        have chosen) a password for access to certain parts of our Website, you
        are responsible for keeping this password confidential and not sharing
        it with anyone. Please note that information collected by third parties
        may not have the same security protections as information you submit to
        us, and we are not responsible for protecting the security of such
        information.
      </p>

      <p>
        <strong>How can I manage or delete information about me?</strong>
      </p>

      <p>
        Depending on your country or state of residence, you have certain rights
        regarding the information that we have about you.
      </p>
      <p>
        Under the General Data Protection Regulation, you have the right to
        access, rectify, port and erase your data. You also have the right to
        object to and restrict certain processing of your data. This includes
        the right to object to our processing of your data where we are
        performing a task in the public interest or pursuing our legitimate
        interests or those of a third party.
      </p>
      <p>
        If you are a California resident, the CCPA grants individuals whose
        information is governed by the CCPA the following rights:
      </p>
      <ul>
        <li>
          Right of information. You can request information about how we have
          collected, used and shared and used your personal information during
          the past 12 months.
        </li>
        <li>
          Right of access. You can request a copy of the personal information
          that we maintain about you.
        </li>
        <li>
          Right of deletion. You can ask us to delete the personal information
          that we collected or maintain about you.
        </li>
      </ul>
      <p>
        You are entitled to exercise the rights described above free from
        discrimination.
      </p>
      <p>
        Please note that applicable laws may limit these rights by, for example,
        prohibiting us from providing certain information in response to an
        access request and limiting the circumstances in which we must comply
        with a deletion request. If we deny your request, we will communicate to
        you our decision.
      </p>
      <p>
        <strong>How long do we retain this information?</strong>
      </p>
      <p>
        Generally, we retain your information for as long as your account is
        active or as needed to provide you the services. If you would like to
        cancel your account or delete your personal information, you may do so
        by contacting us at{" "}
        <a href="mailto:dynabench-site@mlcommons.org">
          dynabench-site@mlcommons.org
        </a>
        .
      </p>
      <p>
        We take measures to delete your information or keep it in a form that
        does not permit identifying you when this information is no longer
        necessary for the purposes for which we process it or when you request
        its deletion, unless we are allowed or required by law to keep the
        information for a longer period, for example to comply with our legal
        obligations, resolve disputes, and enforce our agreements.
      </p>
      <p>
        However, if you request the deletion of your account, certain data (such
        as user-generated content you have uploaded or posted on the Website, as
        well as your interactions with AI systems) will remain and will not be
        deleted. As mentioned above, we take measures to delete or de-identify
        your information, such as by removing from user-generated content your
        username or email address from the author field. We are unable to change
        or delete data used to train AI systems, so you should be careful what
        information you choose to upload or post.
      </p>
      <p>
        <strong>How do we operate and transfer data?</strong>
      </p>
      <p>
        We share your information with partner companies to provide us technical
        support or to provide specific services, such as the hosting of our
        Website, maintenance services and database management.
      </p>
      <p>
        We also share your information with academic and research institutions,
        as well as independent researchers, for research purposes (e.g. to
        improve AI models). Information we may share include user-generated
        content you have uploaded or posted on the Website, as well as your
        interactions with the AI systems. We do not share your account login
        information or name.
      </p>
      <p>
        Partner organizations will have access to your information only to
        perform these services on our behalf or for research purposes only and
        are obligated not to disclose or use it for any other purpose. They may
        be located, or their data processing activities may take place, in the
        United States of America or elsewhere outside the European Economic Area
        (EEA).
      </p>
      <p>
        <strong>
          Our Legal Bases for processing information of individuals in the
          European Union
        </strong>
      </p>
      <p>Our legal bases for processing your information are as follows:</p>
      <ul>
        <li>
          The processing is necessary to perform the contract governing our
          provision of the Website or to take steps that you request prior to
          signing up on the Website.
        </li>
        <li>
          The processing is based on our legitimate interests, such as research
          and development (e.g. training and improving AI models), as well as
          for compliance, fraud prevention and safety purposes.
        </li>
        <li>
          The processing is necessary to comply with our legal obligations.
        </li>
        <li>
          The processing is based on your consent. Where we rely on your consent
          you have the right to withdraw it any time in the manner indicated
          when you consent.
        </li>
      </ul>
      <p>
        <strong>Your rights provided under the GDPR</strong>
      </p>
      <p>
        Under the General Data Protection Regulation, you have the right to
        access, rectify and erase your data under certain circumstances. You
        also have the right to object to and restrict certain processing of your
        data. Please note that to protect your information, we may need to
        verify your identity before processing your request. In some cases, we
        may need to collect additional information to verify your identity, such
        as a government issued ID. To exercise your rights under the GDPR, you
        can email{" "}
        <a href="mailto:dynabench-site@mlcommons.org">
          dynabench-site@mlcommons.org
        </a>{" "}
        or contact us by mail at the address provided below.
      </p>
      <p>
        <strong>South Africa Privacy Notice</strong>
      </p>
      <p>
        This section applies to personal information processing activities to
        which South Africa’s Protection of Personal Information Act 4 of 2013
        (“POPIA”) may apply and supplements the rest of the Dynabench Data
        Policy. You have the right to access, rectify, and erase your personal
        information. To exercise such rights, you can contact us online at
        dynabench-site@mlcommons.org. In certain circumstances, you also have
        the right to object to and restrict the processing of your personal
        information. To exercise these rights you can email{" "}
        <a href="mailto:dynabench-site@mlcommons.org">
          dynabench-site@mlcommons.org
        </a>{" "}
        or contact our Data Protection Officer at the address provided below. We
        will not transfer or transmit your personal information to third parties
        in foreign countries (outside South Africa), unless (i) the recipient of
        your personal information is subject to a law, binding corporate rules
        or binding agreement that provides for an adequate level of protection;
        (ii) you consent to the transfer; (iii) the transfer is necessary to
        fulfill our Dynabench{" "}
        <a href="https://dynabench.org/termsofuse">Terms of Use</a>; (iv) the
        transfer is necessary for the conclusion or performance of a contract
        which we concluded in your interests with another person; or (v) the
        transfer is done for your benefit and it is not reasonably practicable
        to obtain your consent to the transfer and, if it were, you would likely
        have provided your consent. Facebook Inc. provides our services to you.
        You also have the right to lodge a complaint with the Information
        Regulator (South Africa) at the following address: Address: JD House, 27
        Stiemens Street, Braamfontein, Johannesburg, 2001 Post: P.O Box 31533,
        Braamfontein, Johannesburg, 2017 Email: inforeg@justice.gov.za
        (enquiries) / complaints.IR@justice.gov.za (complaints) Website:
        https://justice.gov.za/inforeg/index.html
      </p>
      <p>
        <strong>A note about children</strong>
      </p>
      <p>
        We do not intentionally gather personal information from individuals who
        are under the age of 18. If we learn that a child under 18 has submitted
        personal information to us, we will make commercially reasonable efforts
        to delete the information as soon as possible. If you believe that we
        might have collected any personal information from a child under 18,
        please contact us at{" "}
        <a href="mailto:dynabench-site@mlcommons.org">
          dynabench-site@mlcommons.org
        </a>
        .
      </p>
      <p>
        <strong>Updates to this Data Policy</strong>
      </p>
      <p>
        We may update this Data Policy from time to time. If we modify our Data
        Policy, we will post the revised version here, with an updated revision
        date. We recommend that you visit these pages periodically to be aware
        of and review any such revisions. If we make material changes to our
        Data Policy, we may also notify you by other means prior to the changes
        taking effect, such as by posting a notice on our Website or sending you
        a notification.
      </p>
      <p>
        <strong>Questions</strong>
      </p>
      <p>
        If you have any questions about this Data Policy or our practices,
        please contact us at{" "}
        <a href="mailto:dynabench-site@mlcommons.org">
          dynabench-site@mlcommons.org
        </a>{" "}
        or by mail at:
        <br />
        <br />
        Facebook, Inc.
        <br />
        ATTN: Privacy Operations
        <br />
        1601 Willow Road
        <br />
        Menlo Park, CA 94025
      </p>
    </Col>
  </Container>
);

export default DataPolicyPage;
